import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Collapse, List, ListItemText } from '@mui/material';
//
import { StyledNavItem, StyledNavItemIcon } from './styles';

// ----------------------------------------------------------------------

NavSection.propTypes = {
  data: PropTypes.array,
};

export default function NavSection({ data = [], ...other }) {
  return (
    <Box {...other}>
      <List disablePadding sx={{ p: 1 }}>
        {data.map((item) => (
          <NavItem key={item.title} item={item} />
        ))}
      </List>
    </Box>
  );
}

// ----------------------------------------------------------------------

NavItem.propTypes = {
  item: PropTypes.object,
};

function NavItem({ item }) {
  const { pathname } = useLocation();
  const { title, path, icon, info, children = [] } = item;
  const hasChildren = Array.isArray(children) && children.length > 0;
  const isParentActive = useMemo(
    () => hasChildren && children.some((child) => pathname === child.path),
    [children, hasChildren, pathname]
  );
  const [open, setOpen] = useState(isParentActive);

  useEffect(() => {
    if (isParentActive) {
      setOpen(true);
    }
  }, [isParentActive]);

  if (hasChildren) {
    return (
      <>
        <StyledNavItem
          onClick={() => setOpen((prev) => !prev)}
          sx={{
            mb: 0.5,
            ...(isParentActive && {
              color: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.14),
              fontWeight: 'fontWeightBold',
            }),
          }}
        >
          <StyledNavItemIcon>{icon && icon}</StyledNavItemIcon>
          <ListItemText disableTypography primary={title} />
          <StyledNavItemIcon>
            <Icon icon={open ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} />
          </StyledNavItemIcon>
        </StyledNavItem>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List
            disablePadding
            sx={{
              ml: 3,
              pl: 1,
              borderLeft: (theme) => `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`,
            }}
          >
            {children.map((child) => (
              <StyledNavItem
                key={child.title}
                component={RouterLink}
                to={child.path}
                sx={{
                  mt: 0.5,
                  pl: 2,
                  pr: 1.5,
                  height: 40,
                  borderRadius: 1.25,
                  '&:before': {
                    content: '""',
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    mr: 1,
                    bgcolor: 'text.disabled',
                  },
                  '&.active': {
                    color: 'primary.main',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.14),
                    fontWeight: 'fontWeightBold',
                    '&:before': {
                      bgcolor: 'primary.main',
                    },
                  },
                }}
              >
                <ListItemText disableTypography primary={child.title} />
              </StyledNavItem>
            ))}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <StyledNavItem
      component={RouterLink}
      to={path}
      sx={{
        mb: 0.5,
        '&.active': {
          color: 'primary.main',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.14),
          fontWeight: 'fontWeightBold',
        },
      }}
    >
      <StyledNavItemIcon>{icon && icon}</StyledNavItemIcon>
      <ListItemText disableTypography primary={title} />
      {info && info}
    </StyledNavItem>
  );
}
